import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Halves the stored `shadowBlur` and `shadowDistance` on every text style
 * (`normalStyle`, `highlightStyle`, `spokenWordStyle`).
 *
 * The reusable-texture 2x supersample used to halve on-screen shadow blur/distance, because canvas
 * shadow properties ignore the context transform. That supersample was removed, so shadows render
 * at their full stored value (~2x heavier). Halving the stored values preserves the previous look
 * without a render-time compensation factor.
 *
 * Text styles live on assets in `children`/`stickers`, `subslides[].children`, `textPositionPresets`,
 * and sceneGroup `captions.asset`/`captions.decorations`. `migrateScene` is self-contained so
 * external callers using `Migrator.updateSceneData` / `updateSceneDesignData` get the same result.
 */

const STYLE_FIELDS = ['normalStyle', 'highlightStyle', 'spokenWordStyle'] as const;

const SHADOW_FIELDS = ['shadowBlur', 'shadowDistance'] as const;

function halveAssetShadows(
    map: Immutable.Map<string, unknown>,
    assetPath: Array<string | number>,
): void {
    const asset = map.getIn(assetPath) as Immutable.Map<string, unknown> | undefined;
    if (!asset) return;

    STYLE_FIELDS.forEach((styleField) => {
        const style = asset.get(styleField) as Immutable.Map<string, unknown> | undefined;
        if (!style) return;
        SHADOW_FIELDS.forEach((shadowField) => {
            const value = style.get(shadowField);
            if (typeof value !== 'number') return;
            map.setIn([...assetPath, styleField, shadowField], value / 2);
        });
    });
}

function halveChildrenShadows(
    map: Immutable.Map<string, unknown>,
    childrenPath: Array<string | number>,
): void {
    const children = map.getIn(childrenPath) as Immutable.List<unknown> | undefined;
    if (!children) return;
    children.forEach((_child, i) => {
        halveAssetShadows(map, [...childrenPath, i]);
    });
}

function migrateSceneGroupCaptions(
    sceneGroup: Immutable.Map<string, unknown>,
): Immutable.Map<string, unknown> {
    return sceneGroup.withMutations((map) => {
        if (map.getIn(['captions', 'asset'])) {
            halveAssetShadows(map, ['captions', 'asset']);
        }
        if (map.getIn(['captions', 'decorations'])) {
            halveChildrenShadows(map, ['captions', 'decorations']);
        }
    });
}

export const migrationTo9_244 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            const sceneGroups = map.get('sceneGroups') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (sceneGroups) {
                sceneGroups.forEach((sg, sgIdx) => {
                    map.setIn(['sceneGroups', sgIdx], migrateSceneGroupCaptions(sg));
                });
            }

            const slides = map.get('slides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (slides) {
                slides.forEach((scene, sceneIdx) => {
                    map.setIn(['slides', sceneIdx], this.migrateScene(scene));
                });
            }
        });
    },

    migrateScene(scene, _isSceneDesign) {
        return scene.withMutations((map) => {
            halveChildrenShadows(map, ['children']);
            halveChildrenShadows(map, ['stickers']);

            const subslides = map.get('subslides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (subslides) {
                subslides.forEach((_subslide, subIdx) => {
                    halveChildrenShadows(map, ['subslides', subIdx, 'children']);
                });
            }

            const presets = map.get('textPositionPresets') as
                | Immutable.Map<string, Immutable.List<unknown>>
                | undefined;
            if (presets) {
                presets.forEach((_assets, position) => {
                    halveChildrenShadows(map, ['textPositionPresets', position]);
                });
            }
        });
    },
});
